// Modal for /plan — draft a plan (objective + ordered steps), then approve
// (POST the plan, which seeds the agent's task list and wakes it) or discard
// (close). The decompose step is unchanged: it still drafts ordered steps
// with grounding from the agent's live state.
import { useClient } from '@cortex/react-query';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { Modal } from '../../common/Modal';

import './pa-chat-host.css';

export interface IEditableCriterion {
  content: string;
}

/** What the planner saw of the agent's live state while drafting the steps. */
export interface IPlanGrounding {
  facts: number;
  tools: number;
  capabilities: number;
  learnings: number;
}

/**
 * Human-readable grounding segments, zero counts omitted.
 * `{facts: 3, tools: 0, …}` → `['3 facts', …]`. Empty array = fully ungrounded.
 */
export function groundingSegments(grounding: IPlanGrounding): string[] {
  const parts: Array<[number, string, string]> = [
    [grounding.facts, 'fact', 'facts'],
    [grounding.tools, 'toolset', 'toolsets'],
    [grounding.capabilities, 'capability', 'capabilities'],
    [grounding.learnings, 'lesson', 'lessons'],
  ];
  return parts
    .filter(([count]) => count > 0)
    .map(([count, singular, plural]) => `${count} ${count === 1 ? singular : plural}`);
}

/** Insert a blank step immediately after `index`. */
export function insertStepAfter(rows: IEditableCriterion[], index: number): IEditableCriterion[] {
  const copy = rows.slice();
  copy.splice(index + 1, 0, { content: '' });
  return copy;
}

/** Remove the step at `index`. */
export function removeStepAt(rows: IEditableCriterion[], index: number): IEditableCriterion[] {
  return rows.filter((_, i) => i !== index);
}

/**
 * One-line provenance strip under the objective: what agent context the
 * planner drew on. Status region so screen readers hear when a draft lands.
 */
export function GroundingStrip({
  grounding,
  pending,
}: {
  grounding: IPlanGrounding | null;
  pending: boolean;
}) {
  if (pending) {
    return (
      <div
        className="plan-grounding plan-grounding--pending t-xs t-muted"
        role="status"
        aria-live="polite"
      >
        Reading agent context…
      </div>
    );
  }
  if (!grounding) return null;
  const segments = groundingSegments(grounding);
  return (
    <div className="plan-grounding t-xs t-muted" role="status" aria-live="polite">
      {segments.length === 0 ? (
        'Drafted without agent context'
      ) : (
        <>
          <span className="plan-grounding-dot" aria-hidden="true">
            ●
          </span>
          Grounded in {segments.join(' · ')}
        </>
      )}
    </div>
  );
}

export function PlanProposalModal({
  agentId,
  threadId,
  initialObjective,
  onClose,
  onApproved,
}: {
  agentId: string;
  threadId: string;
  initialObjective: string;
  onClose: () => void;
  onApproved: () => void;
}) {
  const client = useClient();
  const [objective, setObjective] = useState(initialObjective);
  // Seed a single-step draft so Approve is enabled immediately; decompose
  // replaces this when it returns (no need to touch the textarea first).
  const [criteria, setCriteria] = useState<IEditableCriterion[]>(() =>
    initialObjective.trim() ? [{ content: initialObjective.trim() }] : [],
  );
  const [grounding, setGrounding] = useState<IPlanGrounding | null>(null);
  const autoDecomposed = useRef(false);
  // Set when the user picks "Use single step" so a slow auto-decompose that
  // resolves afterward can't overwrite their choice.
  const singleStepChosen = useRef(false);
  // Enter/Backspace step editing: which row to focus after criteria change.
  const stepRefs = useRef<Array<HTMLInputElement | null>>([]);
  const pendingFocus = useRef<number | null>(null);

  const decompose = useMutation({
    mutationFn: () =>
      client.http
        .post(`v1/persistent-agent-groups/${agentId}/agents/${threadId}/objective/decompose`, {
          json: { objective: objective.trim() },
        })
        .json<{
          criteria: Array<{ content: string }>;
          grounding?: IPlanGrounding;
        }>(),
    onSuccess: (data) => {
      if (singleStepChosen.current) return; // user opted for one criterion
      setGrounding(data.grounding ?? null);
      const rows = (data.criteria ?? []).map((c) => ({ content: c.content }));
      setCriteria(rows.length > 0 ? rows : objective.trim() ? [{ content: objective.trim() }] : []);
    },
  });

  const seedPlan = useMutation({
    mutationFn: () => {
      const cleaned = criteria
        .map((c) => ({ content: c.content.trim() }))
        .filter((c) => c.content.length > 0);
      // Always send explicit steps from this modal — never silent autoDecompose
      // on approve (that made /plan feel like it "just ran" without review).
      return client.http
        .post(`v1/persistent-agent-groups/${agentId}/agents/${threadId}/plan`, {
          json: {
            objective: objective.trim(),
            steps: cleaned.length > 0 ? cleaned : [{ content: objective.trim() }],
          },
        })
        .json();
    },
    onSuccess: () => {
      onApproved();
    },
  });

  // Auto-fire decompose once when the slash args already carry an objective.
  useEffect(() => {
    if (autoDecomposed.current) return;
    if (!initialObjective.trim()) return;
    autoDecomposed.current = true;
    decompose.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once on mount
  }, []);

  // Focus the row queued by Enter/Backspace once the list re-renders.
  useEffect(() => {
    const index = pendingFocus.current;
    if (index === null) return;
    pendingFocus.current = null;
    stepRefs.current[index]?.focus();
  }, [criteria]);

  const moveCriterion = (index: number, dir: -1 | 1) => {
    const next = index + dir;
    if (next < 0 || next >= criteria.length) return;
    setCriteria((prev) => {
      const a = prev[index];
      const b = prev[next];
      if (a === undefined || b === undefined) return prev;
      const copy = prev.slice();
      copy[index] = b;
      copy[next] = a;
      return copy;
    });
  };

  // Always allow Approve once there is an objective — seedPlan falls back to
  // a single step from the objective text if the checklist is empty.
  // Do not gate on criteria state or decompose finishing (that forced a
  // dummy edit of the textarea before the button enabled).
  const canApprove = objective.trim().length > 0 && !seedPlan.isPending;

  const cleanedCount = criteria.filter((c) => c.content.trim().length > 0).length;
  const approveLabel = seedPlan.isPending
    ? 'Seeding…'
    : decompose.isPending
      ? 'Approve & seed tasks (drafting in background…)'
      : cleanedCount > 0
        ? `Approve & seed tasks (${cleanedCount} ${cleanedCount === 1 ? 'step' : 'steps'})`
        : 'Approve & seed tasks';

  return (
    <Modal
      open={true}
      onClose={onClose}
      eyebrow="Plan"
      title="Review plan"
      width={560}
      footer={
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--sp-2)',
            flexWrap: 'wrap',
          }}
        >
          <button className="btn btn--ghost btn--sm" onClick={onClose} type="button">
            Discard
          </button>
          <button
            className="btn btn--primary btn--sm"
            disabled={!canApprove}
            onClick={() => seedPlan.mutate()}
            type="button"
            title="Seeds the agent's task list with the current objective and steps"
          >
            {approveLabel}
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <p className="t-xs t-muted" style={{ margin: 0 }}>
          Draft only — approving seeds the agent&apos;s task list and wakes it.
        </p>
        <label className="field">
          <span className="field-label">Objective</span>
          <textarea
            className="input"
            value={objective}
            onChange={(e) => {
              setObjective(e.target.value);
              singleStepChosen.current = false; // editing re-enables drafting
              seedPlan.reset();
              decompose.reset();
            }}
            rows={3}
            style={{ resize: 'vertical', height: 'auto', minHeight: 76 }}
            placeholder="e.g. Patrol the yard and report any open gates"
          />
        </label>

        <GroundingStrip grounding={grounding} pending={decompose.isPending} />

        <div>
          <div className="plan-steps-head">
            <span
              className="t-xs t-muted"
              style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
            >
              Steps (seeded as the agent&apos;s task list)
            </span>
            <span className="plan-steps-head-actions">
              <button
                className="btn btn--sm"
                type="button"
                disabled={!objective.trim() || decompose.isPending}
                onClick={() => {
                  singleStepChosen.current = false;
                  decompose.mutate();
                }}
                title="Draft ordered steps from the objective text"
              >
                {decompose.isPending
                  ? 'Drafting…'
                  : criteria.length
                    ? 'Redraft steps'
                    : 'Draft steps'}
              </button>
              <button
                className="btn btn--ghost btn--sm"
                type="button"
                disabled={!objective.trim()}
                onClick={() => {
                  // Use the typed objective as the single step. Cancel any
                  // in-flight/last draft so it can't overwrite this choice.
                  singleStepChosen.current = true;
                  decompose.reset();
                  setGrounding(null); // drafted steps discarded, provenance with them
                  setCriteria([{ content: objective.trim() }]);
                  seedPlan.reset();
                }}
                title="Skip the AI draft — approve with one step: exactly what you typed"
              >
                Use single step
              </button>
            </span>
          </div>
          {decompose.isPending && criteria.length === 0 ? (
            <div aria-hidden="true">
              <div className="plan-skel" />
              <div className="plan-skel" />
              <div className="plan-skel" />
            </div>
          ) : (
            <>
              <div role="list" aria-label="Plan steps">
                {criteria.map((c, index) => (
                  <div key={index} role="listitem" className="plan-step">
                    <span className="plan-step-num t-xs t-mono t-muted">{index + 1}</span>
                    <input
                      className="input"
                      ref={(el) => {
                        stepRefs.current[index] = el;
                      }}
                      value={c.content}
                      aria-label={`Step ${index + 1}`}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCriteria((prev) =>
                          prev.map((row, i) => (i === index ? { ...row, content: value } : row)),
                        );
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          pendingFocus.current = index + 1;
                          setCriteria((prev) => insertStepAfter(prev, index));
                        } else if (e.key === 'Backspace' && c.content === '' && index > 0) {
                          e.preventDefault();
                          pendingFocus.current = index - 1;
                          setCriteria((prev) => removeStepAt(prev, index));
                        }
                      }}
                      style={{ flex: 1 }}
                    />
                    <span className="plan-step-actions">
                      <button
                        className="btn btn--ghost btn--sm"
                        type="button"
                        disabled={index === 0}
                        onClick={() => moveCriterion(index, -1)}
                        title="Move up"
                        aria-label={`Move step ${index + 1} up`}
                      >
                        ↑
                      </button>
                      <button
                        className="btn btn--ghost btn--sm"
                        type="button"
                        disabled={index === criteria.length - 1}
                        onClick={() => moveCriterion(index, 1)}
                        title="Move down"
                        aria-label={`Move step ${index + 1} down`}
                      >
                        ↓
                      </button>
                      <button
                        className="btn btn--ghost btn--sm"
                        type="button"
                        onClick={() => setCriteria((prev) => removeStepAt(prev, index))}
                        title="Remove step"
                        aria-label={`Remove step ${index + 1}`}
                      >
                        ×
                      </button>
                    </span>
                  </div>
                ))}
              </div>
              <button
                className="btn btn--sm"
                type="button"
                onClick={() => setCriteria((prev) => [...prev, { content: '' }])}
              >
                + Add step
              </button>
            </>
          )}
        </div>

        {(seedPlan.isError || decompose.isError) && (
          <div className="alert alert--danger">
            {seedPlan.isError
              ? String((seedPlan.error as Error)?.message ?? 'Failed to seed plan')
              : String((decompose.error as Error)?.message ?? 'Failed to draft steps')}
          </div>
        )}
      </div>
    </Modal>
  );
}
